import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    TableSortLabel,
    TablePagination,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Title, useDataProvider, useNotify } from 'react-admin';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatApiDateTime, toApiIsoString } from '../admin/dateTimeUtils';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import {
    copyAcademicLiveExamTaskToCore,
    isExamTaskRecordActive,
    isExamTaskRecordPublished,
    resolveExamTaskImageAssetUrl,
    syncLiveExamTaskPublishState,
    syncReviewExamTasksWithPublished,
    type ExamTaskRecord,
} from '../admin/examTaskReviewDataProvider';
import { useExamName } from '../admin/examName';
import { SegmentedTabs, type SegmentedTabOption } from '../admin/SegmentedTabs';
import { ExamTaskDetailContent } from './ExamTaskDetailPage';
import { ExamTaskQuestionNoField, getExamTaskQuestionNo } from './ExamTaskQuestionNo';
import { MediaPreview } from './MediaPreview';
import {
    getDefaultExamTaskTypeForExamName,
    getExamTaskTypeByKey,
    getExamTaskTypeLabel,
    isExamTaskTypeAvailableForExamName,
} from './examTaskTypes';
import {
    getExamTaskPublishTarget,
    getExamTaskWorkflow,
    getVisibleExamTaskWorkflowActions,
    type ExamTaskWorkflowAction,
    type ExamTaskWorkflowId,
} from './examTaskWorkflows';

const publishActionButtonSx = {
    backgroundColor: '#7c3aed',
    borderColor: '#7c3aed',
    color: '#ffffff',
    '&:hover': {
        backgroundColor: '#6d28d9',
        borderColor: '#6d28d9',
    },
};

function formatValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function formatDateTime(value: unknown): string {
    return formatApiDateTime(value);
}

function getTitleValue(record: ExamTaskRecord): string {
    const candidates = [record.displayTitle, record.title, record.taskTitle, record.name, record.id];
    const first = candidates.find((value) => typeof value === 'string' && value.trim());
    return typeof first === 'string' ? first : String(record.id);
}

function getPromptTextValue(record: ExamTaskRecord): string {
    const candidates = [record.promptText, record.promptBody, record.prompt, record.text];
    const first = candidates.find((value) => typeof value === 'string' && value.trim());
    return typeof first === 'string' ? first : 'N/A';
}

function truncateGridText(value: string, maxLength = 150): string {
    if (value.length <= maxLength) {
        return value;
    }

    return `${value.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

function resolveDescribeImageUrl(value: unknown): string {
    return resolveExamTaskImageAssetUrl(value);
}

function getMediaUrlValue(record: ExamTaskRecord): string | null {
    const candidates = [record.mediaResourceUrl, record.mediaUrl, record.imageUrl];
    const first = candidates.find((value) => typeof value === 'string' && value.trim());
    if (typeof first !== 'string') {
        return null;
    }

    return String(record.examTaskType ?? '') === 'DescribeImage' ? resolveDescribeImageUrl(first) : first;
}

function isExamTaskActive(record: ExamTaskRecord): boolean {
    return isExamTaskRecordActive(record);
}

function isExamTaskCopiedFromAcademic(record: ExamTaskRecord): boolean {
    return record.isCopiedFromAcademic === true;
}

function getDetailPageHref(routeBase: string, typeKey: string, recordId: string): string {
    const url = new URL(window.location.href);
    return `${url.origin}${url.pathname}#${routeBase}/${typeKey}/${recordId}`;
}

function formatBulkActionSummary(label: string, successCount: number, failureCount: number): string {
    const normalizedLabel = label.trim() || 'Update';
    if (failureCount === 0) {
        return `${normalizedLabel} completed for ${successCount} exam task${successCount === 1 ? '' : 's'}.`;
    }

    if (successCount === 0) {
        return `${normalizedLabel} failed for all selected exam tasks.`;
    }

    return `${normalizedLabel} completed for ${successCount} exam task${successCount === 1 ? '' : 's'}; ${failureCount} failed.`;
}

type ExamTaskSortField = 'updatedAt' | 'createdAt' | 'displayTitle' | 'id';
type SortOrder = 'ASC' | 'DESC';
type BooleanFilterValue = '' | 'true' | 'false';

const complexityRankTabs: SegmentedTabOption[] = [
    { value: '', label: 'All' },
    { value: 'Easy', label: 'Easy' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Hard', label: 'Hard' },
];
const frequencyTabs: SegmentedTabOption[] = [
    { value: '', label: 'Any' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Monthly', label: 'Monthly' },
    { value: 'All', label: 'All' },
];
const publishedTabs: SegmentedTabOption[] = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Published' },
    { value: 'false', label: 'Unpublished' },
];
const premiumTabs: SegmentedTabOption[] = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Premium' },
    { value: 'false', label: 'Free' },
];
const activeTabs: SegmentedTabOption[] = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
];
const copiedFromAcademicTabs: SegmentedTabOption[] = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Copied' },
    { value: 'false', label: 'Direct' },
];
const sortFieldTabs: SegmentedTabOption[] = [
    { value: 'id', label: 'ID' },
    { value: 'displayTitle', label: 'Title' },
    { value: 'updatedAt', label: 'Updated Date' },
    { value: 'createdAt', label: 'Created Date' },
];
const sortOrderTabs: SegmentedTabOption[] = [
    { value: 'DESC', label: 'Newest First' },
    { value: 'ASC', label: 'Oldest First' },
];
const complexityRankOptions = ['Easy', 'Medium', 'Hard'] as const;
const frequencyOptions = ['Weekly', 'Monthly', 'All'] as const;
const allowedDescribeImageExtensions = new Set(['jpg', 'jpeg', 'png', 'webp']);
const maxDescribeImageSizeInBytes = 10 * 1024 * 1024;
const describeImageAccept = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';

type ExamTaskFilterState = {
    id: string;
    questionNo: string;
    displayTitle: string;
    complexityRank: string;
    freq: string;
    mediaCategory: string;
    isPremiumContent: BooleanFilterValue;
    isActive: BooleanFilterValue;
    isPublished: BooleanFilterValue;
    isCopiedFromAcademic: BooleanFilterValue;
    createdAtFrom: string;
    createdAtTo: string;
    updatedAtFrom: string;
    updatedAtTo: string;
};

const defaultExamTaskFilters: ExamTaskFilterState = {
    id: '',
    questionNo: '',
    displayTitle: '',
    complexityRank: '',
    freq: '',
    mediaCategory: '',
    isPremiumContent: '',
    isActive: '',
    isPublished: '',
    isCopiedFromAcademic: '',
    createdAtFrom: '',
    createdAtTo: '',
    updatedAtFrom: '',
    updatedAtTo: '',
};

type CreateExamTaskFormState = {
    displayTitle: string;
    promptBody: string;
    complexityRank: string;
    freq: string;
    mediaResourceUrl: string;
    mediaCategory: string;
    discoveryTags: string;
    answerChoices: string;
    configSettings: string;
    segments: string;
    isPremiumContent: boolean;
};

function getDefaultCreateExamTaskFormState(): CreateExamTaskFormState {
    return {
        displayTitle: '',
        promptBody: '',
        complexityRank: 'Medium',
        freq: '',
        mediaResourceUrl: '',
        mediaCategory: '',
        discoveryTags: '',
        answerChoices: '',
        configSettings: '',
        segments: '',
        isPremiumContent: false,
    };
}

function parseJsonField(value: string, label: string): unknown | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        throw new Error(`${label} must be valid JSON`);
    }
}

function parseJsonOrStringField(value: string): unknown | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        return trimmed;
    }
}

function normalizeAnswerChoicesValue(value: string): string | null {
    const parsed = parseJsonOrStringField(value);
    if (parsed === null) {
        return null;
    }

    return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
}

function normalizeFormDataKeySegment(value: string): string {
    return value
        .replace(/[_-]+([a-zA-Z0-9])/g, (_, character: string) => character.toUpperCase())
        .replace(/^./, (character) => character.toUpperCase());
}

function appendFormDataValue(formData: FormData, key: string, value: unknown): void {
    if (value === undefined || value === null || value === '') {
        return;
    }

    if (value instanceof File) {
        formData.append(key, value);
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            appendFormDataValue(formData, `${key}[${index}]`, item);
        });
        return;
    }

    if (typeof value === 'object') {
        Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
            appendFormDataValue(formData, `${key}.${normalizeFormDataKeySegment(childKey)}`, childValue);
        });
        return;
    }

    formData.append(key, String(value));
}

function validateDescribeImageFile(file: File): string | null {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (!allowedDescribeImageExtensions.has(extension)) {
        return 'Describe Image uploads must be JPG, JPEG, PNG, or WEBP files.';
    }

    if (file.size === 0 || file.size > maxDescribeImageSizeInBytes) {
        return 'Describe Image uploads must be between 1 byte and 10 MB.';
    }

    return null;
}

function buildCreateExamTaskFormData(
    form: CreateExamTaskFormState,
    examName: string,
    examTaskType: string,
    describeImageFile: File | null,
): FormData {
    const segments = parseJsonField(form.segments, 'Segments');
    if (segments !== null && !Array.isArray(segments)) {
        throw new Error('Segments must be a JSON array');
    }

    const formData = new FormData();
    appendFormDataValue(formData, 'DisplayTitle', form.displayTitle.trim());
    appendFormDataValue(formData, 'PromptBody', form.promptBody.trim());
    appendFormDataValue(formData, 'ComplexityRank', form.complexityRank.trim());
    appendFormDataValue(formData, 'Freq', form.freq.trim() || null);
    appendFormDataValue(formData, 'ExamTaskType', examTaskType);
    appendFormDataValue(formData, 'IsPremiumContent', form.isPremiumContent);
    appendFormDataValue(formData, 'MediaResourceUrl', form.mediaResourceUrl.trim() || null);
    appendFormDataValue(formData, 'MediaCategory', form.mediaCategory.trim() || null);
    appendFormDataValue(formData, 'DiscoveryTags', form.discoveryTags.trim());
    appendFormDataValue(formData, 'AnswerChoices', normalizeAnswerChoicesValue(form.answerChoices));
    appendFormDataValue(formData, 'ConfigSettings', parseJsonField(form.configSettings, 'Config settings'));
    appendFormDataValue(formData, 'ExamName', examName);
    appendFormDataValue(formData, 'Segments', segments ?? []);

    if (describeImageFile) {
        formData.append('DescribeImageFile', describeImageFile);
    }

    return formData;
}

function getDefaultExamTaskFilters(isLiveWorkflow: boolean): ExamTaskFilterState {
    return {
        ...defaultExamTaskFilters,
        isActive: isLiveWorkflow ? 'true' : '',
        isPublished: isLiveWorkflow ? '' : 'false',
    };
}

function toIsoDateTime(value: string): string | undefined {
    if (!value.trim()) return undefined;
    return toApiIsoString(value) ?? undefined;
}

function buildExamTaskApiFilter(filters: ExamTaskFilterState, includeActivationFilters: boolean) {
    return {
        ...(filters.id.trim() ? { id: filters.id.trim() } : {}),
        ...(filters.questionNo.trim() ? { questionNo: filters.questionNo.trim() } : {}),
        ...(filters.displayTitle.trim() ? { displayTitle: filters.displayTitle.trim() } : {}),
        ...(filters.complexityRank.trim() ? { complexityRank: filters.complexityRank.trim() } : {}),
        ...(includeActivationFilters && filters.freq.trim() ? { freq: filters.freq.trim() } : {}),
        ...(filters.mediaCategory.trim() ? { mediaCategory: filters.mediaCategory.trim() } : {}),
        ...(includeActivationFilters && filters.isPremiumContent
            ? { isPremiumContent: filters.isPremiumContent === 'true' }
            : {}),
        ...(includeActivationFilters && filters.isActive ? { isActive: filters.isActive === 'true' } : {}),
        ...(includeActivationFilters && filters.isPublished ? { isPublished: filters.isPublished === 'true' } : {}),
        ...(includeActivationFilters && filters.isCopiedFromAcademic
            ? { isCopiedFromAcademic: filters.isCopiedFromAcademic === 'true' }
            : {}),
        ...(!includeActivationFilters && filters.isPublished ? { IsPublished: filters.isPublished === 'true' } : {}),
        ...(toIsoDateTime(filters.createdAtFrom) ? { createdAtFrom: toIsoDateTime(filters.createdAtFrom) } : {}),
        ...(toIsoDateTime(filters.createdAtTo) ? { createdAtTo: toIsoDateTime(filters.createdAtTo) } : {}),
        ...(toIsoDateTime(filters.updatedAtFrom) ? { updatedAtFrom: toIsoDateTime(filters.updatedAtFrom) } : {}),
        ...(toIsoDateTime(filters.updatedAtTo) ? { updatedAtTo: toIsoDateTime(filters.updatedAtTo) } : {}),
    };
}

type ExamTaskTypePageProps = {
    workflow?: ExamTaskWorkflowId;
};

type PendingLiveConfirmation =
    | {
        kind: 'bulk-action';
        action: ExamTaskWorkflowAction;
        eligibleRecords: ExamTaskRecord[];
        skippedCount: number;
    }
    | {
        kind: 'bulk-copy-to-core';
        records: ExamTaskRecord[];
    }
    | {
        kind: 'row-action';
        action: ExamTaskWorkflowAction;
        record: ExamTaskRecord;
    }
    | {
        kind: 'copy-to-core';
        record: ExamTaskRecord;
    };

export function ExamTaskTypePage({ workflow: workflowId = 'review' }: ExamTaskTypePageProps) {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const publishTarget = getExamTaskPublishTarget();
    const match = getExamTaskTypeByKey(type);
    const workflow = getExamTaskWorkflow(workflowId);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { examName, setExamName } = useExamName();
    const currentExamTaskLabel = getExamTaskTypeLabel(match, examName);
    const isLiveWorkflow = workflow.id === 'destination';
    const defaultFilters = useMemo(() => getDefaultExamTaskFilters(isLiveWorkflow), [isLiveWorkflow]);
    const canCopyAcademicToCore = workflow.id === 'destination'
        && examName === 'Academic'
        && Boolean(match)
        && isExamTaskTypeAvailableForExamName(match?.key, 'Core');

    const [items, setItems] = useState<ExamTaskRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortFieldDraft, setSortFieldDraft] = useState<ExamTaskSortField>('updatedAt');
    const [sortField, setSortField] = useState<ExamTaskSortField>('updatedAt');
    const [sortOrderDraft, setSortOrderDraft] = useState<SortOrder>('DESC');
    const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
    const [filterDraft, setFilterDraft] = useState<ExamTaskFilterState>(defaultFilters);
    const [filters, setFilters] = useState<ExamTaskFilterState>(defaultFilters);
    const [loading, setLoading] = useState(false);
    const [selectedExamTaskId, setSelectedExamTaskId] = useState<string | null>(null);
    const [selectedExamTaskIds, setSelectedExamTaskIds] = useState<string[]>([]);
    const [bulkActionKey, setBulkActionKey] = useState<string | null>(null);
    const [rowActionKey, setRowActionKey] = useState<string | null>(null);
    const [syncWithPublishedPending, setSyncWithPublishedPending] = useState(false);
    const [syncPublishStatePending, setSyncPublishStatePending] = useState(false);
    const [reloadVersion, setReloadVersion] = useState(0);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createPending, setCreatePending] = useState(false);
    const [createForm, setCreateForm] = useState<CreateExamTaskFormState>(() => getDefaultCreateExamTaskFormState());
    const [createDescribeImageFile, setCreateDescribeImageFile] = useState<File | null>(null);
    const [pendingLiveConfirmation, setPendingLiveConfirmation] = useState<PendingLiveConfirmation | null>(null);

    useEffect(() => {
        setPage(1);
        setSearch('');
        setSearchInput('');
        setItems([]);
        setTotal(0);
        setSelectedExamTaskId(null);
        setSelectedExamTaskIds([]);
        setSortFieldDraft('updatedAt');
        setSortField('updatedAt');
        setSortOrderDraft('DESC');
        setSortOrder('DESC');
        setFilterDraft(defaultFilters);
        setFilters(defaultFilters);
        setCreateDialogOpen(false);
        setCreatePending(false);
        setCreateForm(getDefaultCreateExamTaskFormState());
        setCreateDescribeImageFile(null);
        setPendingLiveConfirmation(null);
    }, [defaultFilters, match?.key]);

    useEffect(() => {
        setPage(1);
    }, [examName]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setPage(1);
            setSearch(searchInput.trim());
            setSortField(sortFieldDraft);
            setSortOrder(sortOrderDraft);
            setFilters(filterDraft);
        }, 300);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [filterDraft, searchInput, sortFieldDraft, sortOrderDraft]);

    useEffect(() => {
        if (!match) {
            return;
        }

        const nextExamName = searchParams.get('examName');
        const nextQuestionNo = searchParams.get('questionNo')?.trim() ?? '';
        const nextSearch = searchParams.get('q')?.trim() ?? '';

        if (nextExamName === 'Academic' || nextExamName === 'Core') {
            setExamName(nextExamName);
        }

        setPage(1);
        setSearchInput(nextSearch);
        setSearch(nextSearch);
        setFilterDraft((current) => ({
            ...current,
            questionNo: nextQuestionNo,
        }));
        setFilters((current) => ({
            ...current,
            questionNo: nextQuestionNo,
        }));
    }, [match, searchParams, setExamName]);

    useEffect(() => {
        setSelectedExamTaskIds((current) => {
            const visibleIds = new Set(items.map((record) => String(record.id)));
            return current.filter((id) => visibleIds.has(id));
        });
    }, [items]);

    useEffect(() => {
        if (!match || isExamTaskTypeAvailableForExamName(match.key, examName)) {
            return;
        }

        const fallbackExamTaskType = getDefaultExamTaskTypeForExamName(examName);
        if (!fallbackExamTaskType) {
            return;
        }

        notify(`${currentExamTaskLabel} is not available for ${examName}. Showing ${getExamTaskTypeLabel(fallbackExamTaskType, examName)} instead.`, {
            type: 'info',
        });
        navigate(`${workflow.routeBase}/${fallbackExamTaskType.key}`, { replace: true });
    }, [currentExamTaskLabel, examName, match, navigate, notify, workflow.routeBase]);

    useEffect(() => {
        if (!match || workflow.missingControllerMessage) {
            setItems([]);
            setTotal(0);
            setLoading(false);
            return;
        }

        if (!match) return;

        let active = true;

        const load = async () => {
            setLoading(true);
            try {
                const apiFilters = buildExamTaskApiFilter(filters, isLiveWorkflow);
                const response = await dataProvider.getList(workflow.resource, {
                    pagination: { page, perPage },
                    sort: { field: sortField, order: sortOrder },
                    filter: {
                        examTaskType: match.apiType,
                        examName,
                        q: search,
                        ...apiFilters,
                    },
                });

                if (!active) return;
                const records = response.data as ExamTaskRecord[];
                setItems(records);
                setTotal(response.total ?? records.length);
            } catch (error) {
                if (!active) return;
                setItems([]);
                setTotal(0);
                notify(error instanceof Error ? error.message : 'Failed to load exam tasks', {
                    type: 'error',
                });
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            active = false;
        };
    }, [
        dataProvider,
        examName,
        filters,
        isLiveWorkflow,
        match,
        notify,
        page,
        perPage,
        search,
        sortField,
        sortOrder,
        workflow.missingControllerMessage,
        workflow.resource,
        reloadVersion,
    ]);

    const handleCloseDetail = () => {
        setSelectedExamTaskId(null);
    };

    const handleQuestionNoCopied = (value: string) => {
        notify(`Question No copied: ${value}`, { type: 'info' });
    };

    const handleQuestionNoCopyError = (error: unknown) => {
        notify(error instanceof Error ? error.message : 'Unable to copy Question No.', { type: 'warning' });
    };

    const handleDetailMutation = () => {
        setSelectedExamTaskId(null);
        setReloadVersion((current) => current + 1);
    };

    const allVisibleExamTaskIds = items.map((record) => String(record.id));
    const hasSelections = selectedExamTaskIds.length > 0;
    const hasVisibleItems = allVisibleExamTaskIds.length > 0;
    const allVisibleSelected = hasVisibleItems && allVisibleExamTaskIds.every((id) => selectedExamTaskIds.includes(id));
    const partiallySelected = hasSelections && !allVisibleSelected;
    const selectedRecords = items.filter((record) => selectedExamTaskIds.includes(String(record.id)));
    const selectedInactiveCount = selectedRecords.filter((record) => !isExamTaskActive(record)).length;
    const selectedActiveCount = selectedRecords.filter((record) => isExamTaskActive(record)).length;
    const selectedPublishedCount = selectedRecords.filter((record) => isExamTaskRecordPublished(record)).length;
    const selectionDisabled = Boolean(bulkActionKey) || Boolean(rowActionKey) || loading;
    const confirmationPending = Boolean(bulkActionKey) || Boolean(rowActionKey);

    const renderExamModePill = (label: string) => {
        const isCore = label.trim().toLowerCase() === 'core';

        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    lineHeight: 1,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#ffffff',
                    background: isCore
                        ? 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)'
                        : 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)',
                    boxShadow: isCore
                        ? '0 10px 24px rgba(20, 184, 166, 0.28)'
                        : '0 10px 24px rgba(37, 99, 235, 0.28)',
                    animation: 'examModePillPulse 1.8s ease-in-out infinite',
                    '@keyframes examModePillPulse': {
                        '0%': {
                            transform: 'translateY(0) scale(1)',
                            boxShadow: isCore
                                ? '0 10px 24px rgba(20, 184, 166, 0.28)'
                                : '0 10px 24px rgba(37, 99, 235, 0.28)',
                        },
                        '50%': {
                            transform: 'translateY(-1px) scale(1.04)',
                            boxShadow: isCore
                                ? '0 14px 28px rgba(20, 184, 166, 0.34)'
                                : '0 14px 28px rgba(37, 99, 235, 0.34)',
                        },
                        '100%': {
                            transform: 'translateY(0) scale(1)',
                            boxShadow: isCore
                                ? '0 10px 24px rgba(20, 184, 166, 0.28)'
                                : '0 10px 24px rgba(37, 99, 235, 0.28)',
                        },
                    },
                }}
            >
                {label}
            </Box>
        );
    };

    const getConfirmationTitle = () => {
        if (!pendingLiveConfirmation) {
            return 'Confirm Action';
        }

        if (pendingLiveConfirmation.kind === 'bulk-copy-to-core') {
            return 'Bulk Copy to Core';
        }

        if (pendingLiveConfirmation.kind === 'copy-to-core') {
            return 'Copy to Core';
        }

        if (pendingLiveConfirmation.action.key === 'publish') {
            return `Publish to ${publishTarget.title}`;
        }

        return pendingLiveConfirmation.action.label;
    };

    const getConfirmationBody = () => {
        if (!pendingLiveConfirmation) {
            return '';
        }

        if (pendingLiveConfirmation.kind === 'bulk-copy-to-core') {
            const count = pendingLiveConfirmation.records.length;
            return `Are you sure you want to copy ${count} selected Academic exam task${count === 1 ? '' : 's'} to Core?`;
        }

        if (pendingLiveConfirmation.kind === 'copy-to-core') {
            return `Are you sure you want to copy ${getTitleValue(pendingLiveConfirmation.record)} to Core?`;
        }

        if (pendingLiveConfirmation.kind === 'bulk-action') {
            const count = pendingLiveConfirmation.eligibleRecords.length;
            return `${pendingLiveConfirmation.action.label} ${count} selected exam task${count === 1 ? '' : 's'}?`;
        }

        if (pendingLiveConfirmation.action.requiresConfirmation) {
            return pendingLiveConfirmation.action.confirmationMessage?.(getTitleValue(pendingLiveConfirmation.record))
                ?? 'Are you sure you want to run this action?';
        }

        if (pendingLiveConfirmation.action.key === 'publish') {
            return `Are you sure you want to publish ${getTitleValue(pendingLiveConfirmation.record)} to ${publishTarget.noun}?`;
        }

        return `${pendingLiveConfirmation.action.label} ${getTitleValue(pendingLiveConfirmation.record)}?`;
    };

    const getConfirmationNote = () => {
        if (!pendingLiveConfirmation) {
            return '';
        }

        if (pendingLiveConfirmation.kind === 'bulk-copy-to-core') {
            return 'If you click Yes, each selected Academic exam task will be copied into live Core exam tasks.';
        }

        if (pendingLiveConfirmation.kind === 'copy-to-core') {
            return 'If you click Yes, a live Core exam task will be created from this Academic exam task.';
        }

        if (pendingLiveConfirmation.kind === 'bulk-action') {
            if (pendingLiveConfirmation.skippedCount > 0) {
                return pendingLiveConfirmation.action.key === 'activate'
                    ? `${pendingLiveConfirmation.skippedCount} selected exam task${pendingLiveConfirmation.skippedCount === 1 ? '' : 's'} already active and will be skipped.`
                    : pendingLiveConfirmation.action.key === 'deactivate'
                        ? `${pendingLiveConfirmation.skippedCount} selected exam task${pendingLiveConfirmation.skippedCount === 1 ? '' : 's'} already inactive and will be skipped.`
                        : `${pendingLiveConfirmation.skippedCount} selected exam task${pendingLiveConfirmation.skippedCount === 1 ? '' : 's'} already published and will be skipped.`;
            }

            return 'This bulk action runs only after you click Yes.';
        }

        if (pendingLiveConfirmation.action.key === 'publish') {
            return `This exam task will be published to ${publishTarget.noun} only if you click Yes.`;
        }

        return 'This action runs only after you click Yes.';
    };

    const getConfirmationConfirmButtonColor = () => {
        if (
            !pendingLiveConfirmation
            || pendingLiveConfirmation.kind === 'copy-to-core'
            || pendingLiveConfirmation.kind === 'bulk-copy-to-core'
        ) {
            return 'primary' as const;
        }

        return pendingLiveConfirmation.action.color;
    };

    const getConfirmationConfirmButtonLabel = () => {
        if (!pendingLiveConfirmation) {
            return 'Yes';
        }

        if (
            pendingLiveConfirmation.kind === 'copy-to-core'
            || pendingLiveConfirmation.kind === 'bulk-copy-to-core'
        ) {
            return 'Yes';
        }

        return pendingLiveConfirmation.action.pendingLabel;
    };

    const handleToggleExamTaskSelection = (recordId: string, checked: boolean) => {
        setSelectedExamTaskIds((current) => {
            if (checked) {
                return current.includes(recordId) ? current : [...current, recordId];
            }

            return current.filter((id) => id !== recordId);
        });
    };

    const handleToggleSelectAll = (checked: boolean) => {
        setSelectedExamTaskIds(checked ? allVisibleExamTaskIds : []);
    };

    const replaceExamTaskRecord = (nextRecord: ExamTaskRecord) => {
        setItems((current) => current.map((record) => (String(record.id) === String(nextRecord.id) ? nextRecord : record)));
    };

    const removeExamTaskRecord = (recordId: string) => {
        setItems((current) => current.filter((record) => String(record.id) !== recordId));
        setSelectedExamTaskIds((current) => current.filter((id) => id !== recordId));
        setTotal((current) => Math.max(current - 1, 0));
    };

    const handleSortRequest = (field: ExamTaskSortField) => {
        const nextOrder: SortOrder = sortField === field && sortOrder === 'DESC' ? 'ASC' : 'DESC';
        setPage(1);
        setSortFieldDraft(field);
        setSortField(field);
        setSortOrderDraft(nextOrder);
        setSortOrder(nextOrder);
        setReloadVersion((current) => current + 1);
    };

    const handleApplyFilters = () => {
        setPage(1);
        setSearch(searchInput.trim());
        setSortField(sortFieldDraft);
        setSortOrder(sortOrderDraft);
        setFilters(filterDraft);
        setReloadVersion((current) => current + 1);
    };

    const handleClearFilters = () => {
        setPage(1);
        setSearchInput('');
        setSearch('');
        setSortFieldDraft('updatedAt');
        setSortField('updatedAt');
        setSortOrderDraft('DESC');
        setSortOrder('DESC');
        setFilterDraft(defaultFilters);
        setFilters(defaultFilters);
        setReloadVersion((current) => current + 1);
    };

    const handleSyncWithPublished = async () => {
        setSyncWithPublishedPending(true);
        try {
            const result = await syncReviewExamTasksWithPublished();
            notify(
                `Sync completed. Processed ${result.processedCount} review exam tasks; matched ${result.matchedCount}; marked ${result.markedPublishedCount} as published; ${result.unmatchedCount} unmatched.`,
                { type: 'info' },
            );
            setSelectedExamTaskIds([]);
            setSelectedExamTaskId(null);
            setReloadVersion((current) => current + 1);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to sync review exam tasks with published tasks.', {
                type: 'error',
            });
        } finally {
            setSyncWithPublishedPending(false);
        }
    };

    const handleSyncPublishState = async () => {
        setSyncPublishStatePending(true);
        try {
            const result = await syncLiveExamTaskPublishState();
            notify(
                `Publish state sync completed. Local ${result.defaultTaskCount}; production ${result.productionTaskCount}; matched ${result.matchedQuestionNoCount}; already published ${result.alreadyPublishedCount}; updated ${result.updatedCount}; unmatched ${result.unmatchedCount}.`,
                { type: 'info' },
            );
            setSelectedExamTaskIds([]);
            setSelectedExamTaskId(null);
            setReloadVersion((current) => current + 1);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to sync live exam task publish state.', {
                type: 'error',
            });
        } finally {
            setSyncPublishStatePending(false);
        }
    };

    const handleOpenCreateDialog = () => {
        setCreateForm(getDefaultCreateExamTaskFormState());
        setCreateDescribeImageFile(null);
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        if (createPending) {
            return;
        }

        setCreateDialogOpen(false);
        setCreateDescribeImageFile(null);
    };

    const handleCreateFieldChange = (field: keyof CreateExamTaskFormState, value: string | boolean) => {
        setCreateForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleCreateExamTask = async () => {
        if (!match || workflow.id !== 'review') {
            return;
        }

        if (!createForm.displayTitle.trim()) {
            notify('Display Title is required.', { type: 'warning' });
            return;
        }

        if (!createForm.complexityRank.trim()) {
            notify('Complexity Rank is required.', { type: 'warning' });
            return;
        }

        if (String(match.apiType) === 'DescribeImage' && createDescribeImageFile) {
            const validationMessage = validateDescribeImageFile(createDescribeImageFile);
            if (validationMessage) {
                notify(validationMessage, { type: 'error' });
                return;
            }
        }

        setCreatePending(true);
        try {
            const payload = buildCreateExamTaskFormData(createForm, examName, match.apiType, createDescribeImageFile);
            const response = await dataProvider.create(workflow.resource, { data: payload });
            const createdRecord = response.data as ExamTaskRecord;

            setCreateDialogOpen(false);
            setCreateForm(getDefaultCreateExamTaskFormState());
            setCreateDescribeImageFile(null);
            setPage(1);
            setSelectedExamTaskId(String(createdRecord.id));
            setReloadVersion((current) => current + 1);
            notify('Exam task created.', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to create exam task.', {
                type: 'error',
            });
        } finally {
            setCreatePending(false);
        }
    };

    const handleBulkReviewAction = async (action: (typeof workflow.actions)[number]) => {
        if (workflow.id !== 'review' || !selectedExamTaskIds.length) {
            return;
        }

        if (!selectedRecords.length) {
            return;
        }

        const eligibleRecords =
            action.key === 'approve'
                ? selectedRecords.filter((record) => !isExamTaskRecordPublished(record))
                : selectedRecords;

        if (!eligibleRecords.length) {
            notify('All selected exam tasks are already published and cannot be accepted again.', {
                type: 'info',
            });
            return;
        }

        const skippedCount = selectedRecords.length - eligibleRecords.length;

        const confirmed = window.confirm(
            `${action.label} ${eligibleRecords.length} selected exam task${eligibleRecords.length === 1 ? '' : 's'}?`,
        );
        if (!confirmed) {
            return;
        }

        setBulkActionKey(action.key);
        try {
            const results = await Promise.allSettled(
                eligibleRecords.map(async (record) => {
                    await dataProvider.update(workflow.resource, {
                        id: record.id,
                        data: record,
                        previousData: record,
                        meta: {
                            action: action.key,
                            actionPath: action.actionPath,
                        },
                    });

                    return String(record.id);
                }),
            );

            const succeededIds = results
                .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
                .map((result) => result.value);
            const failedResults = results.filter(
                (result): result is PromiseRejectedResult => result.status === 'rejected',
            );

            if (succeededIds.length > 0) {
                setSelectedExamTaskIds((current) => current.filter((id) => !succeededIds.includes(id)));
                setReloadVersion((current) => current + 1);
            }

            const message = formatBulkActionSummary(action.label, succeededIds.length, failedResults.length);
            notify(message, {
                type:
                    failedResults.length > 0
                        ? succeededIds.length > 0
                            ? 'warning'
                            : 'error'
                        : 'info',
            });

            if (failedResults.length > 0) {
                const firstReason = failedResults[0].reason;
                const failureMessage = firstReason instanceof Error ? firstReason.message : action.errorMessage;
                notify(failureMessage, { type: 'error' });
            }

            if (skippedCount > 0 && action.key === 'approve') {
                notify(
                    `${skippedCount} selected exam task${skippedCount === 1 ? '' : 's'} already published and skipped.`,
                    { type: 'info' },
                );
            }
        } finally {
            setBulkActionKey(null);
        }
    };

    const runBulkLiveAction = async (
        action: ExamTaskWorkflowAction,
        eligibleRecords: ExamTaskRecord[],
        skippedCount: number,
    ) => {
        if (workflow.id !== 'destination' || !eligibleRecords.length) {
            return;
        }

        setBulkActionKey(action.key);
        try {
            const results = await Promise.allSettled(
                eligibleRecords.map(async (record) => {
                    await dataProvider.update(workflow.resource, {
                        id: record.id,
                        data: {},
                        previousData: record,
                        meta: {
                            action: action.key,
                            actionPath: action.actionPath,
                            method: action.requestMethod,
                        },
                    });

                    return String(record.id);
                }),
            );

            const succeededIds = results
                .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
                .map((result) => result.value);
            const failedResults = results.filter(
                (result): result is PromiseRejectedResult => result.status === 'rejected',
            );

            if (succeededIds.length > 0) {
                setSelectedExamTaskIds((current) => current.filter((id) => !succeededIds.includes(id)));
                setReloadVersion((current) => current + 1);
            }

            const message = formatBulkActionSummary(action.label, succeededIds.length, failedResults.length);
            notify(message, {
                type:
                    failedResults.length > 0
                        ? succeededIds.length > 0
                            ? 'warning'
                            : 'error'
                        : 'info',
            });

            if (skippedCount > 0) {
                notify(
                    action.key === 'activate'
                        ? `${skippedCount} selected exam task${skippedCount === 1 ? '' : 's'} already active and skipped.`
                        : action.key === 'deactivate'
                            ? `${skippedCount} selected exam task${skippedCount === 1 ? '' : 's'} already inactive and skipped.`
                            : `${skippedCount} selected exam task${skippedCount === 1 ? '' : 's'} already published and skipped.`,
                    { type: 'info' },
                );
            }

            if (failedResults.length > 0) {
                const firstReason = failedResults[0].reason;
                const failureMessage = firstReason instanceof Error ? firstReason.message : action.errorMessage;
                notify(failureMessage, { type: 'error' });
            }
        } finally {
            setBulkActionKey(null);
        }
    };

    const handleBulkLiveAction = (action: ExamTaskWorkflowAction) => {
        if (workflow.id !== 'destination' || !selectedExamTaskIds.length) {
            return;
        }

        if (!action.actionPath) {
            notify('This live exam task action is not configured.', { type: 'warning' });
            return;
        }

        const eligibleRecords = selectedRecords.filter((record) => {
            if (action.key === 'activate') {
                return !isExamTaskActive(record);
            }

            if (action.key === 'deactivate') {
                return isExamTaskActive(record);
            }

            if (action.key === 'publish') {
                return !isExamTaskRecordPublished(record);
            }

            return true;
        });

        if (!eligibleRecords.length) {
            notify(
                action.key === 'activate'
                    ? 'No inactive exam tasks selected.'
                    : action.key === 'deactivate'
                        ? 'No active exam tasks selected.'
                        : `No unpublished exam tasks selected for ${publishTarget.noun}.`,
                {
                    type: 'info',
                },
            );
            return;
        }

        const skippedCount = selectedRecords.length - eligibleRecords.length;
        setPendingLiveConfirmation({
            kind: 'bulk-action',
            action,
            eligibleRecords,
            skippedCount,
        });
    };

    const runLiveRowAction = async (record: ExamTaskRecord, action: ExamTaskWorkflowAction) => {
        if (workflow.id !== 'destination') {
            return;
        }

        if (!action.actionPath) {
            notify('This live exam task action is not configured.', { type: 'warning' });
            return;
        }

        const pendingKey = `${action.key}:${String(record.id)}`;
        setRowActionKey(pendingKey);
        try {
            await dataProvider.update(workflow.resource, {
                id: record.id,
                data: {},
                previousData: record,
                meta: {
                    action: action.key,
                    actionPath: action.actionPath,
                    method: action.requestMethod,
                },
            });

            notify(action.successMessage(currentExamTaskLabel ?? 'Exam Task'), { type: 'info' });
            if (action.removesRecord && selectedExamTaskId === String(record.id)) {
                setSelectedExamTaskId(null);
            }

            if (action.removesRecord) {
                removeExamTaskRecord(String(record.id));
                return;
            }

            const response = await dataProvider.getOne(workflow.resource, { id: record.id });
            replaceExamTaskRecord(response.data as ExamTaskRecord);
        } catch (error) {
            notify(error instanceof Error ? error.message : action.errorMessage, { type: 'error' });
        } finally {
            setRowActionKey(null);
        }
    };

    const handleLiveRowAction = async (record: ExamTaskRecord, action: ExamTaskWorkflowAction) => {
        if (workflow.id !== 'destination') {
            return;
        }

        if (!action.actionPath) {
            notify('This live exam task action is not configured.', { type: 'warning' });
            return;
        }

        setPendingLiveConfirmation({ kind: 'row-action', record, action });
    };

    const handleRequestCopyAcademicToCore = (record: ExamTaskRecord) => {
        setPendingLiveConfirmation({ kind: 'copy-to-core', record });
    };

    const handleRequestBulkCopyAcademicToCore = () => {
        if (!canCopyAcademicToCore || !selectedRecords.length) {
            return;
        }

        setPendingLiveConfirmation({
            kind: 'bulk-copy-to-core',
            records: selectedRecords,
        });
    };

    const handleCopyAcademicToCore = async (record: ExamTaskRecord) => {
        if (!match || !canCopyAcademicToCore) {
            return;
        }

        const pendingKey = `copyAcademicToCore:${String(record.id)}`;
        setRowActionKey(pendingKey);
        try {
            await copyAcademicLiveExamTaskToCore(String(record.id));
            notify(`${getTitleValue(record)} copied to Core.`, { type: 'info' });
            setReloadVersion((current) => current + 1);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to copy the Academic exam task to Core.', {
                type: 'error',
            });
        } finally {
            setRowActionKey(null);
        }
    };

    const handleBulkCopyAcademicToCore = async (records: ExamTaskRecord[]) => {
        if (!match || !canCopyAcademicToCore || !records.length) {
            return;
        }

        setBulkActionKey('copyAcademicToCore');
        try {
            const succeededIds: string[] = [];
            let failureCount = 0;
            let firstFailureMessage: string | null = null;

            for (const record of records) {
                try {
                    await copyAcademicLiveExamTaskToCore(String(record.id));
                    succeededIds.push(String(record.id));
                } catch (error) {
                    failureCount += 1;
                    if (!firstFailureMessage) {
                        firstFailureMessage = error instanceof Error
                            ? error.message
                            : 'Failed to copy the selected Academic exam task to Core.';
                    }
                }
            }

            if (succeededIds.length > 0) {
                setSelectedExamTaskIds((current) => current.filter((id) => !succeededIds.includes(id)));
            }

            const summary = failureCount === 0
                ? `Copied ${succeededIds.length} exam task${succeededIds.length === 1 ? '' : 's'} to Core.`
                : succeededIds.length === 0
                    ? 'Failed to copy all selected exam tasks to Core.'
                    : `Copied ${succeededIds.length} exam task${succeededIds.length === 1 ? '' : 's'} to Core; ${failureCount} failed.`;

            notify(summary, {
                type: failureCount > 0 ? (succeededIds.length > 0 ? 'warning' : 'error') : 'info',
            });

            if (firstFailureMessage) {
                notify(firstFailureMessage, { type: 'error' });
            }

            if (succeededIds.length > 0) {
                setReloadVersion((current) => current + 1);
            }
        } finally {
            setBulkActionKey(null);
        }
    };

    const livePrimaryAction =
        workflow.id === 'destination'
            ? workflow.actions.find(
                (action) => action.key === 'primary' && action.actionPath && action.actionPath !== 'update',
            ) ?? null
            : null;
    const liveTableActions =
        workflow.id === 'destination'
            ? livePrimaryAction
                ? [livePrimaryAction, ...workflow.actions.filter((action) => action.key !== 'primary')]
                : workflow.actions.filter((action) => action.key !== 'primary')
            : [];
    const liveBulkActions =
        workflow.id === 'destination'
            ? workflow.actions.filter(
                (action) => action.key === 'activate' || action.key === 'deactivate' || action.key === 'publish',
            )
            : [];

    return (
        <Card sx={{ width: '100%' }}>
            <Title title={workflow.pageTitle} />
            <CardContent sx={{ width: '100%' }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {currentExamTaskLabel ?? type ?? 'Exam Task Type'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {workflow.listDescription(examName, match?.apiType)}
                        </Typography>
                    </Box>

                    {!match ? (
                        <Alert severity="warning">Unknown exam task type.</Alert>
                    ) : workflow.missingControllerMessage ? (
                        <Alert severity="warning">{workflow.missingControllerMessage}</Alert>
                    ) : (
                        <>
                            <Stack direction="row" spacing={2} alignItems="flex-start" useFlexGap flexWrap="wrap">
                                <TextField
                                    label="Search"
                                    value={searchInput}
                                    onChange={(event) => setSearchInput(event.target.value)}
                                    size="small"
                                    sx={{ minWidth: 320, flexGrow: 1 }}
                                />
                                {workflow.id === 'review' ? (
                                    <Button variant="contained" color="primary" onClick={handleOpenCreateDialog}>
                                        New Question
                                    </Button>
                                ) : null}
                                <Button variant="contained" onClick={handleApplyFilters}>
                                    Apply Filters
                                </Button>
                                <Button variant="outlined" onClick={handleClearFilters}>
                                    Clear
                                </Button>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                }}
                            >
                                <TextField
                                    label="Exam Task ID"
                                    size="small"
                                    value={filterDraft.id}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, id: event.target.value }))
                                    }
                                />
                                <TextField
                                    label="Question No"
                                    size="small"
                                    value={filterDraft.questionNo}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, questionNo: event.target.value }))
                                    }
                                />
                                <TextField
                                    label="Title"
                                    size="small"
                                    value={filterDraft.displayTitle}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, displayTitle: event.target.value }))
                                    }
                                />
                                <SegmentedTabs
                                    label="Complexity"
                                    value={filterDraft.complexityRank}
                                    options={complexityRankTabs}
                                    onChange={(nextValue) =>
                                        setFilterDraft((current) => ({ ...current, complexityRank: nextValue }))
                                    }
                                />
                                {isLiveWorkflow ? (
                                    <SegmentedTabs
                                        label="Frequency"
                                        value={filterDraft.freq}
                                        options={frequencyTabs}
                                        onChange={(nextValue) =>
                                            setFilterDraft((current) => ({ ...current, freq: nextValue }))
                                        }
                                    />
                                ) : null}
                                <TextField
                                    label="Media Category"
                                    size="small"
                                    value={filterDraft.mediaCategory}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, mediaCategory: event.target.value }))
                                    }
                                />
                                <SegmentedTabs
                                    label="Is Published"
                                    value={filterDraft.isPublished}
                                    options={publishedTabs}
                                    onChange={(nextValue) =>
                                        setFilterDraft((current) => ({
                                            ...current,
                                            isPublished: nextValue as BooleanFilterValue,
                                        }))
                                    }
                                />
                                {!isLiveWorkflow ? null : (
                                    <>
                                        <SegmentedTabs
                                            label="Premium"
                                            value={filterDraft.isPremiumContent}
                                            options={premiumTabs}
                                            onChange={(nextValue) =>
                                                setFilterDraft((current) => ({
                                                    ...current,
                                                    isPremiumContent: nextValue as BooleanFilterValue,
                                                }))
                                            }
                                        />
                                        <SegmentedTabs
                                            label="Active"
                                            value={filterDraft.isActive}
                                            options={activeTabs}
                                            onChange={(nextValue) =>
                                                setFilterDraft((current) => ({
                                                    ...current,
                                                    isActive: nextValue as BooleanFilterValue,
                                                }))
                                            }
                                        />
                                        <SegmentedTabs
                                            label="Copied From Academic"
                                            value={filterDraft.isCopiedFromAcademic}
                                            options={copiedFromAcademicTabs}
                                            onChange={(nextValue) =>
                                                setFilterDraft((current) => ({
                                                    ...current,
                                                    isCopiedFromAcademic: nextValue as BooleanFilterValue,
                                                }))
                                            }
                                        />
                                    </>
                                )}
                                <TextField
                                    label="Created From"
                                    type="datetime-local"
                                    size="small"
                                    value={filterDraft.createdAtFrom}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, createdAtFrom: event.target.value }))
                                    }
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <TextField
                                    label="Created To"
                                    type="datetime-local"
                                    size="small"
                                    value={filterDraft.createdAtTo}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, createdAtTo: event.target.value }))
                                    }
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <TextField
                                    label="Updated From"
                                    type="datetime-local"
                                    size="small"
                                    value={filterDraft.updatedAtFrom}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, updatedAtFrom: event.target.value }))
                                    }
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <TextField
                                    label="Updated To"
                                    type="datetime-local"
                                    size="small"
                                    value={filterDraft.updatedAtTo}
                                    onChange={(event) =>
                                        setFilterDraft((current) => ({ ...current, updatedAtTo: event.target.value }))
                                    }
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                }}
                            >
                                <SegmentedTabs
                                    label="Sort By"
                                    value={sortFieldDraft}
                                    options={sortFieldTabs}
                                    onChange={(nextValue) => setSortFieldDraft(nextValue as ExamTaskSortField)}
                                />
                                <SegmentedTabs
                                    label="Sort Order"
                                    value={sortOrderDraft}
                                    options={sortOrderTabs}
                                    onChange={(nextValue) => setSortOrderDraft(nextValue as SortOrder)}
                                />
                            </Box>

                            {workflow.id === 'review' ? (
                                <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                        {hasSelections
                                            ? `${selectedExamTaskIds.length} exam task${selectedExamTaskIds.length === 1 ? '' : 's'} selected on this page.`
                                            : 'Select exam tasks to run bulk review actions.'}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        disabled={Boolean(bulkActionKey) || loading || syncWithPublishedPending}
                                        onClick={() => void handleSyncWithPublished()}
                                    >
                                        {syncWithPublishedPending ? 'Syncing...' : 'Sync With Published'}
                                    </Button>
                                    {workflow.actions.map((action) => {
                                        const disabledForSelection =
                                            action.key === 'approve'
                                                ? selectedRecords.length > 0 && selectedPublishedCount === selectedRecords.length
                                                : false;

                                        return (
                                            <Button
                                                key={action.key}
                                                variant={action.color === 'error' ? 'outlined' : 'contained'}
                                                color={action.color}
                                                disabled={!hasSelections || Boolean(bulkActionKey) || loading || disabledForSelection}
                                                onClick={() => void handleBulkReviewAction(action)}
                                            >
                                                {bulkActionKey === action.key ? action.pendingLabel : `Bulk ${action.label}`}
                                            </Button>
                                        );
                                    })}
                                </Stack>
                            ) : null}

                            {workflow.id === 'destination' ? (
                                <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                        {hasSelections
                                            ? `${selectedExamTaskIds.length} exam task${selectedExamTaskIds.length === 1 ? '' : 's'} selected on this page.`
                                            : canCopyAcademicToCore
                                                ? `Select exam tasks on this page to bulk copy to Core, activate, deactivate, or publish to ${publishTarget.noun}.`
                                                : `Select exam tasks on this page to bulk activate, deactivate, or publish to ${publishTarget.noun}.`}
                                    </Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        <Button
                                            variant="outlined"
                                            disabled={loading || syncPublishStatePending || confirmationPending}
                                            onClick={() => void handleSyncPublishState()}
                                        >
                                            {syncPublishStatePending ? 'Syncing Publish State...' : 'Sync Publish State'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            disabled={!hasVisibleItems || allVisibleSelected || selectionDisabled}
                                            onClick={() => handleToggleSelectAll(true)}
                                        >
                                            Select All on Page
                                        </Button>
                                        <Button
                                            variant="text"
                                            disabled={!hasSelections || selectionDisabled}
                                            onClick={() => handleToggleSelectAll(false)}
                                        >
                                            Clear Selection
                                        </Button>
                                        {canCopyAcademicToCore ? (
                                            <Button
                                                variant="contained"
                                                disabled={!hasSelections || selectionDisabled}
                                                onClick={() => handleRequestBulkCopyAcademicToCore()}
                                            >
                                                {bulkActionKey === 'copyAcademicToCore' ? 'Bulk Copying...' : 'Bulk Copy to Core'}
                                            </Button>
                                        ) : null}
                                        {liveBulkActions.map((action) => {
                                            const disabledForSelection =
                                                action.key === 'activate'
                                                    ? selectedInactiveCount === 0
                                                    : action.key === 'deactivate'
                                                        ? selectedActiveCount === 0
                                                        : action.key === 'publish'
                                                            ? selectedPublishedCount === selectedRecords.length
                                                        : !hasSelections;

                                            return (
                                                <Button
                                                    key={action.key}
                                                    variant={action.color === 'error' ? 'outlined' : 'contained'}
                                                    color={action.color}
                                                    sx={action.key === 'publish' ? publishActionButtonSx : undefined}
                                                    disabled={!hasSelections || selectionDisabled || disabledForSelection}
                                                    onClick={() => void handleBulkLiveAction(action)}
                                                >
                                                    {bulkActionKey === action.key ? action.pendingLabel : `Bulk ${action.label}`}
                                                </Button>
                                            );
                                        })}
                                    </Stack>
                                </Stack>
                            ) : null}

                            {loading ? (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Loading exam tasks...
                                    </Typography>
                                </Stack>
                            ) : items.length === 0 ? (
                                <Alert severity="info">No exam tasks found for this type.</Alert>
                            ) : (
                                    <TableContainer
                                        sx={{
                                            width: '100%',
                                            overflowX: 'auto',
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table
                                            size="small"
                                            sx={{
                                                width: '100%',
                                                tableLayout: 'fixed',
                                                '& th, & td': {
                                                    verticalAlign: 'top',
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word',
                                                },
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    {workflow.id === 'review' || isLiveWorkflow ? (
                                                        <TableCell padding="checkbox" sx={{ fontWeight: 700, width: 72 }}>
                                                            <Checkbox
                                                                checked={allVisibleSelected}
                                                                indeterminate={partiallySelected}
                                                                disabled={!hasVisibleItems || selectionDisabled}
                                                                onChange={(event) => handleToggleSelectAll(event.target.checked)}
                                                                inputProps={{ 'aria-label': 'Select all exam tasks on this page' }}
                                                            />
                                                        </TableCell>
                                                    ) : null}
                                                    {workflow.id === 'review' ? (
                                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Published</TableCell>
                                                    ) : null}
                                                    {workflow.id === 'destination' ? (
                                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Published</TableCell>
                                                    ) : null}
                                                    {workflow.id === 'destination' ? (
                                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Copied</TableCell>
                                                    ) : null}
                                                    <DesktopActionHeaderCell width={120} sx={{ fontWeight: 700 }}>
                                                        View
                                                    </DesktopActionHeaderCell>
                                                    <TableCell sx={{ fontWeight: 700, width: '12%' }}>
                                                        <TableSortLabel
                                                            active={sortField === 'id'}
                                                            direction={sortField === 'id' ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'desc'}
                                                            onClick={() => handleSortRequest('id')}
                                                        >
                                                            ID
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, width: '15%' }}>
                                                        <TableSortLabel
                                                            active={sortField === 'displayTitle'}
                                                            direction={sortField === 'displayTitle' ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'desc'}
                                                            onClick={() => handleSortRequest('displayTitle')}
                                                        >
                                                            Title
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, width: '24%' }}>Prompt Text</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, width: '12%' }}>Media</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, width: '12%' }}>
                                                        <TableSortLabel
                                                            active={sortField === 'createdAt'}
                                                            direction={sortField === 'createdAt' ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'desc'}
                                                            onClick={() => handleSortRequest('createdAt')}
                                                        >
                                                            Created
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 700, width: '12%' }}>
                                                        <TableSortLabel
                                                            active={sortField === 'updatedAt'}
                                                            direction={sortField === 'updatedAt' ? sortOrder.toLowerCase() as 'asc' | 'desc' : 'desc'}
                                                            onClick={() => handleSortRequest('updatedAt')}
                                                        >
                                                            Updated
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    {isLiveWorkflow ? (
                                                        <TableCell sx={{ fontWeight: 700, width: '8%' }}>Status</TableCell>
                                                    ) : null}
                                                    {isLiveWorkflow
                                                        ? liveTableActions.map((action) => (
                                                            <DesktopActionHeaderCell
                                                                key={action.key}
                                                                sx={{ fontWeight: 700 }}
                                                                width="10%"
                                                            >
                                                                {action.label}
                                                            </DesktopActionHeaderCell>
                                                        ))
                                                        : null}
                                                    {canCopyAcademicToCore ? (
                                                        <DesktopActionHeaderCell sx={{ fontWeight: 700 }} width="10%">
                                                            Copy To Core
                                                        </DesktopActionHeaderCell>
                                                    ) : null}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {items.map((record) => {
                                                    const title = getTitleValue(record);
                                                    const promptText = getPromptTextValue(record);
                                                    const promptTextPreview = truncateGridText(promptText);
                                                    const mediaUrl = getMediaUrlValue(record);
                                                    const questionNo = getExamTaskQuestionNo(record);
                                                    const isActive = isExamTaskActive(record);
                                                    const isPublished = isExamTaskRecordPublished(record);
                                                    const isCopiedFromAcademic = isExamTaskCopiedFromAcademic(record);
                                                    const liveActions = getVisibleExamTaskWorkflowActions(workflow, isActive);
                                                    const liveRowActions = livePrimaryAction ? [livePrimaryAction, ...liveActions] : liveActions;
                                                    const detailRoute = match ? `${workflow.routeBase}/${match.key}/${record.id}` : undefined;
                                                    const detailHref = match
                                                        ? getDetailPageHref(workflow.routeBase, match.key, String(record.id))
                                                        : '#';

                                                    return (
                                                        <TableRow key={record.id} hover>
                                                            {workflow.id === 'review' || isLiveWorkflow ? (
                                                                <TableCell padding="checkbox">
                                                                    <Checkbox
                                                                        checked={selectedExamTaskIds.includes(String(record.id))}
                                                                        disabled={selectionDisabled}
                                                                        onChange={(event) =>
                                                                            handleToggleExamTaskSelection(String(record.id), event.target.checked)
                                                                        }
                                                                        inputProps={{
                                                                            'aria-label': `Select exam task ${String(record.id)}`,
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            ) : null}
                                                            {workflow.id === 'review' ? (
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={isPublished ? 'success.main' : 'text.secondary'}
                                                                        fontWeight={600}
                                                                    >
                                                                        {isPublished ? 'Published' : 'Unpublished'}
                                                                    </Typography>
                                                                </TableCell>
                                                            ) : null}
                                                            {workflow.id === 'destination' ? (
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={isPublished ? 'success.main' : 'text.secondary'}
                                                                        fontWeight={600}
                                                                    >
                                                                        {isPublished ? 'Published' : 'Unpublished'}
                                                                    </Typography>
                                                                </TableCell>
                                                            ) : null}
                                                            {workflow.id === 'destination' ? (
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={isCopiedFromAcademic ? 'info.main' : 'text.secondary'}
                                                                        fontWeight={600}
                                                                    >
                                                                        {isCopiedFromAcademic ? 'Copied' : 'Direct'}
                                                                    </Typography>
                                                                </TableCell>
                                                            ) : null}
                                                            <DesktopActionCell>
                                                                <Stack spacing={0.75} alignItems="flex-start">
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            if (detailRoute) {
                                                                                setSelectedExamTaskId(String(record.id));
                                                                            }
                                                                        }}
                                                                    >
                                                                        View
                                                                    </Button>
                                                                    <Tooltip title="View in new tab">
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                component="a"
                                                                                href={detailHref}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                disabled={!detailRoute}
                                                                                aria-label="View in new tab"
                                                                            >
                                                                                <OpenInNewIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </DesktopActionCell>
                                                            <TableCell>
                                                                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                                                    <Box title={String(record.id)}>{String(record.id)}</Box>
                                                                    <ExamTaskQuestionNoField
                                                                        value={questionNo}
                                                                        onCopied={handleQuestionNoCopied}
                                                                        onCopyError={handleQuestionNoCopyError}
                                                                    />
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box title={title} sx={{ width: '100%', display: 'block' }}>
                                                                    {title}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box title={promptText} sx={{ width: '100%', display: 'block' }}>
                                                                    {promptTextPreview}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                {mediaUrl ? (
                                                                    <MediaPreview url={mediaUrl} title={title} width={96} height={64} />
                                                                ) : (
                                                                    formatValue(null)
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{formatDateTime(record.createdAt)}</TableCell>
                                                            <TableCell>{formatDateTime(record.updatedAt)}</TableCell>
                                                            {isLiveWorkflow ? (
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={isActive ? 'success.main' : 'text.secondary'}
                                                                        fontWeight={600}
                                                                    >
                                                                        {isActive ? 'Active' : 'Inactive'}
                                                                    </Typography>
                                                                </TableCell>
                                                            ) : null}
                                                            {isLiveWorkflow
                                                                ? liveTableActions.map((tableAction) => {
                                                                    const rowAction = liveRowActions.find((action) => action.key === tableAction.key) ?? null;

                                                                    return (
                                                                        <DesktopActionCell key={tableAction.key}>
                                                                            {rowAction ? (
                                                                                <Button
                                                                                    variant={rowAction.color === 'error' ? 'outlined' : 'contained'}
                                                                                    color={rowAction.color}
                                                                                    disabled={Boolean(bulkActionKey) || Boolean(rowActionKey) || loading || !rowAction.actionPath}
                                                                                    onClick={() => void handleLiveRowAction(record, rowAction)}
                                                                                >
                                                                                    {rowActionKey === `${rowAction.key}:${String(record.id)}`
                                                                                        ? rowAction.pendingLabel
                                                                                        : rowAction.label}
                                                                                </Button>
                                                                            ) : null}
                                                                        </DesktopActionCell>
                                                                    );
                                                                })
                                                                : null}
                                                            {canCopyAcademicToCore ? (
                                                                <DesktopActionCell>
                                                                    <Button
                                                                        variant="contained"
                                                                        disabled={Boolean(bulkActionKey) || Boolean(rowActionKey) || loading}
                                                                        onClick={() => handleRequestCopyAcademicToCore(record)}
                                                                    >
                                                                        {rowActionKey === `copyAcademicToCore:${String(record.id)}`
                                                                            ? 'Copying...'
                                                                            : 'Copy to Core'}
                                                                    </Button>
                                                                </DesktopActionCell>
                                                            ) : null}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                            )}

                            <TablePagination
                                component="div"
                                count={total}
                                page={Math.max(page - 1, 0)}
                                onPageChange={(_, nextPage) => setPage(nextPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(event) => {
                                    setPerPage(Number(event.target.value));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 500]}
                            />

                            <Dialog
                                open={createDialogOpen}
                                onClose={handleCloseCreateDialog}
                                fullWidth
                                maxWidth="md"
                            >
                                <DialogTitle>New Question</DialogTitle>
                                <DialogContent dividers>
                                    <Stack spacing={2} sx={{ pt: 1 }}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                            <TextField
                                                label="Exam Name"
                                                value={examName}
                                                fullWidth
                                                disabled
                                            />
                                            <TextField
                                                label="Exam Task Type"
                                                value={match?.apiType ?? ''}
                                                fullWidth
                                                disabled
                                            />
                                        </Stack>
                                        <TextField
                                            label="Display Title"
                                            value={createForm.displayTitle}
                                            onChange={(event) => handleCreateFieldChange('displayTitle', event.target.value)}
                                            required
                                            fullWidth
                                        />
                                        <TextField
                                            label="Prompt Body"
                                            value={createForm.promptBody}
                                            onChange={(event) => handleCreateFieldChange('promptBody', event.target.value)}
                                            multiline
                                            minRows={4}
                                            fullWidth
                                        />
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                            <TextField
                                                label="Complexity Rank"
                                                select
                                                value={createForm.complexityRank}
                                                onChange={(event) => handleCreateFieldChange('complexityRank', event.target.value)}
                                                required
                                                fullWidth
                                            >
                                                {complexityRankOptions.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                label="Frequency"
                                                select
                                                value={createForm.freq}
                                                onChange={(event) => handleCreateFieldChange('freq', event.target.value)}
                                                fullWidth
                                            >
                                                <MenuItem value="">Not set</MenuItem>
                                                {frequencyOptions.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                label="Media Category"
                                                value={createForm.mediaCategory}
                                                onChange={(event) => handleCreateFieldChange('mediaCategory', event.target.value)}
                                                fullWidth
                                            />
                                        </Stack>
                                        <TextField
                                            label="Media Resource URL"
                                            value={createForm.mediaResourceUrl}
                                            onChange={(event) => handleCreateFieldChange('mediaResourceUrl', event.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Discovery Tags"
                                            value={createForm.discoveryTags}
                                            onChange={(event) => handleCreateFieldChange('discoveryTags', event.target.value)}
                                            helperText="Sent as plain text unless you provide valid JSON elsewhere."
                                            multiline
                                            minRows={2}
                                            fullWidth
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={createForm.isPremiumContent}
                                                    onChange={(event) =>
                                                        handleCreateFieldChange('isPremiumContent', event.target.checked)
                                                    }
                                                />
                                            }
                                            label="Premium content"
                                        />
                                        <TextField
                                            label="Answer Choices"
                                            value={createForm.answerChoices}
                                            onChange={(event) => handleCreateFieldChange('answerChoices', event.target.value)}
                                            helperText="Optional. Plain text is allowed; valid JSON is preserved as JSON."
                                            multiline
                                            minRows={4}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Config Settings"
                                            value={createForm.configSettings}
                                            onChange={(event) => handleCreateFieldChange('configSettings', event.target.value)}
                                            helperText="Optional JSON object."
                                            multiline
                                            minRows={4}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Segments"
                                            value={createForm.segments}
                                            onChange={(event) => handleCreateFieldChange('segments', event.target.value)}
                                            helperText="Optional JSON array."
                                            multiline
                                            minRows={6}
                                            fullWidth
                                        />
                                        {String(match?.apiType ?? '') === 'DescribeImage' ? (
                                            <Stack spacing={1}>
                                                <Button component="label" variant="outlined">
                                                    {createDescribeImageFile ? 'Replace Image File' : 'Choose Image File'}
                                                    <input
                                                        hidden
                                                        type="file"
                                                        accept={describeImageAccept}
                                                        onChange={(event) => {
                                                            const nextFile = event.target.files?.[0] ?? null;
                                                            event.target.value = '';

                                                            if (!nextFile) {
                                                                return;
                                                            }

                                                            const validationMessage = validateDescribeImageFile(nextFile);
                                                            if (validationMessage) {
                                                                notify(validationMessage, { type: 'error' });
                                                                return;
                                                            }

                                                            setCreateDescribeImageFile(nextFile);
                                                        }}
                                                    />
                                                </Button>
                                                <Typography variant="body2" color="text.secondary">
                                                    {createDescribeImageFile
                                                        ? `Selected file: ${createDescribeImageFile.name}`
                                                        : 'Optional JPG, JPEG, PNG, or WEBP file up to 10 MB.'}
                                                </Typography>
                                            </Stack>
                                        ) : null}
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseCreateDialog} disabled={createPending}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => void handleCreateExamTask()}
                                        disabled={createPending || !match}
                                    >
                                        {createPending ? 'Creating...' : 'Create'}
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            <Dialog
                                open={Boolean(selectedExamTaskId)}
                                onClose={handleCloseDetail}
                                fullWidth
                                maxWidth="xl"
                            >
                                <DialogTitle sx={{ pr: 7 }}>
                                    {currentExamTaskLabel ?? 'Exam Task Detail'}
                                    <IconButton
                                        aria-label="Close"
                                        onClick={handleCloseDetail}
                                        sx={{ position: 'absolute', right: 8, top: 8 }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent dividers>
                                    {selectedExamTaskId ? (
                                        <ExamTaskDetailContent
                                            workflow={workflow}
                                            examTaskType={match?.key}
                                            examTaskId={selectedExamTaskId}
                                            onReviewActionCompleted={handleDetailMutation}
                                            onRecordDeleted={handleDetailMutation}
                                        />
                                    ) : null}
                                </DialogContent>
                            </Dialog>

                            <Dialog
                                open={pendingLiveConfirmation !== null}
                                onClose={() => setPendingLiveConfirmation(null)}
                                maxWidth="xs"
                                fullWidth
                            >
                                <DialogTitle sx={{ pb: 1.5 }}>
                                    <Stack spacing={1.25}>
                                        <Typography variant="h6">{getConfirmationTitle()}</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                Exam
                                            </Typography>
                                            {pendingLiveConfirmation?.kind === 'copy-to-core' ? (
                                                <>
                                                    {renderExamModePill('Academic')}
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                        to
                                                    </Typography>
                                                    {renderExamModePill('Core')}
                                                </>
                                            ) : pendingLiveConfirmation?.kind === 'bulk-copy-to-core' ? (
                                                <>
                                                    {renderExamModePill('Academic')}
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                                        to
                                                    </Typography>
                                                    {renderExamModePill('Core')}
                                                </>
                                            ) : (
                                                renderExamModePill(examName)
                                            )}
                                        </Stack>
                                    </Stack>
                                </DialogTitle>
                                <DialogContent>
                                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                                        <Typography variant="body1">{getConfirmationBody()}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {getConfirmationNote()}
                                        </Typography>
                                    </Stack>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setPendingLiveConfirmation(null)} disabled={confirmationPending}>
                                        No
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color={getConfirmationConfirmButtonColor()}
                                        sx={
                                            pendingLiveConfirmation?.kind !== 'copy-to-core'
                                            && pendingLiveConfirmation?.kind !== 'bulk-copy-to-core'
                                            && pendingLiveConfirmation?.action.key === 'publish'
                                                ? publishActionButtonSx
                                                : undefined
                                        }
                                        disabled={confirmationPending || !pendingLiveConfirmation}
                                        onClick={() => {
                                            if (!pendingLiveConfirmation) {
                                                return;
                                            }

                                            const nextConfirmation = pendingLiveConfirmation;
                                            setPendingLiveConfirmation(null);

                                            if (nextConfirmation.kind === 'copy-to-core') {
                                                void handleCopyAcademicToCore(nextConfirmation.record);
                                                return;
                                            }

                                            if (nextConfirmation.kind === 'bulk-copy-to-core') {
                                                void handleBulkCopyAcademicToCore(nextConfirmation.records);
                                                return;
                                            }

                                            if (nextConfirmation.kind === 'bulk-action') {
                                                void runBulkLiveAction(
                                                    nextConfirmation.action,
                                                    nextConfirmation.eligibleRecords,
                                                    nextConfirmation.skippedCount,
                                                );
                                                return;
                                            }

                                            void runLiveRowAction(nextConfirmation.record, nextConfirmation.action);
                                        }}
                                    >
                                        {confirmationPending ? getConfirmationConfirmButtonLabel() : 'Yes'}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}